<template>
  <a-modal
    :title="isEdit ? `编辑产品: ${productKey}` : '新建产品'"
    :visible="visible"
    :width="960"
    :footer="null"
    destroyOnClose
    wrapClassName="product-create-dialog"
    @cancel="handleClose"
  >
    <!-- 类型切换 -->
    <div class="type-row">
      <span class="type-label">类型</span>
      <a-select
        v-model="form.type"
        style="width: 160px"
        :disabled="isEdit"
        @change="onTypeChange"
      >
        <a-select-option value="SINGLE">SINGLE 单票</a-select-option>
        <a-select-option value="PKG">PKG 套餐</a-select-option>
      </a-select>
    </div>

    <!-- ========== SINGLE 单票 ========== -->
    <template v-if="form.type === 'SINGLE'">
      <!-- 1. 选择产品 event -->
      <div class="section-block">
        <div class="section-title">
          <span class="section-index">1</span>
          选择产品 event
        </div>
        <div class="event-row">
          <a-select
            v-model="form.eventId"
            showSearch
            optionFilterProp="children"
            placeholder="请选择 event"
            style="flex: 1"
            :loading="eventLoading || ticketLoading"
            @change="onEventChange"
          >
            <a-select-option
              v-for="event in eventList"
              :key="event.eventId"
              :value="event.eventId"
            >
              {{ event.eventId }} [{{ event.eventType }}] {{ event.name }}
            </a-select-option>
          </a-select>
          <a-button type="primary" :loading="eventLoading" @click="onFetchEvent">拉取</a-button>
        </div>
        <div class="event-info" v-if="selectedEvent">
          已选 <b>{{ selectedEvent.eventId }}</b> · {{ selectedEvent.eventType }} 型 
        </div>
      </div>

      <!-- 2. 票型(option)设置 -->
      <div class="section-block">
        <div class="section-title">
          <span class="section-index">2</span>
          票型(option)设置
          <span class="section-tip">（点击「新增票型」，每票型均为该 event 的 ticket）</span>
        </div>
        <div class="option-notice">{{ optionNoticeText }}</div>

        <a-spin :spinning="ticketLoading">
          <div v-if="!form.eventId" class="option-empty">请先选择产品 event</div>
          <template v-else>
            <div
              v-for="(group, groupIndex) in form.optionGroups"
              :key="groupIndex"
              class="option-card"
            >
              <div class="option-card-head">
                <a-input
                  v-model="group.optionName"
                  class="option-card-title-input"
                  size="small"
                  :placeholder="`票型 ${groupIndex + 1}`"
                  allowClear
                />
                <div class="option-card-actions">
                  <div v-if="isRType" class="fallback-inline">
                    <span class="fallback-inline-label">兜底价:</span>
                    <a-input-number
                      v-model="group.fallbackPriceHkd"
                      :min="0"
                      :precision="2"
                      placeholder="请输入兜底价"
                      size="small"
                      style="width: 120px"
                    />
                  </div>
                  <div class="fallback-inline">
                    <span class="fallback-inline-label">追加:</span>
                    <a-input-number
                      v-model="group.addSalePrice"
                      :min="0"
                      :precision="2"
                      placeholder="请输入追加价"
                      size="small"
                      style="width: 120px"
                    />
                  </div>
                  <a-button
                    type="danger"
                    ghost
                    shape="circle"
                    icon="close"
                    size="small"
                    @click="onRemoveOptionGroup(groupIndex)"
                  />
                </div>
              </div>
              <div v-if="!ticketList.length" class="option-empty">暂无可选票，请确认 event 是否正确</div>
              <div v-else class="ticket-groups">
                <div
                  v-for="bucket in ticketGroupOptions"
                  :key="bucket.key"
                  class="ticket-group"
                >
                  <div class="ticket-group-label">{{ bucket.label }}</div>
                  <div class="ticket-grid">
                    <div
                      v-for="ticket in bucket.tickets"
                      :key="ticket.ticketId"
                      class="ticket-chip"
                      :class="{
                        'is-selected': isTicketSelected(group, ticket.ticketId),
                        'is-disabled': isTicketTakenByOther(group, ticket.ticketId)
                      }"
                      @click="onToggleTicket(group, ticket)"
                    >
                      {{ ticket.name }}
                      <span class="ticket-id">{{ ticket.ticketId }}</span>
                      <span v-if="ticket.price != null && ticket.price !== ''" class="ticket-price">HK${{ ticket.price }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="ticket-selected-info">已选 {{ group.ticketIds.length }} 票：{{ selectedTicketText(group) }}</div>
            </div>

            <div v-if="!form.optionGroups.length" class="option-empty">还没有票型 —— 点下面「+ 新增票型」</div>
            <a-button class="add-option-btn" type="dashed" block icon="plus" @click="onAddOptionGroup">新增票型</a-button>
          </template>
        </a-spin>
        <div class="option-footnote">
          一个 <b>ticketid</b> 只能归一个票型——已归属某票型的票，在其他票型里自动置灰不可选（强校验）。
        </div>
      </div>

      <!-- 3. 产品名 & 保存（编辑时不展示产品名步骤，仅保留保存按钮） -->
      <div class="section-block">
        <template v-if="!isEdit">
          <div class="section-title">
            <span class="section-index">3</span>
            产品名 &amp; 保存
          </div>
          <div class="save-form-item">
            <a-input v-model="form.productName" placeholder="如「三次畅游优惠」" />
          </div>
        </template>
        <a-button type="primary" block class="save-btn" :loading="saveLoading" @click="onSaveEvent">
          {{ isEdit ? '保存修改' : '保存产品' }}
        </a-button>
      </div>
    </template>

    <!-- ========== PKG 套餐 ========== -->
    <template v-else>
      <!-- 1. 套餐名 & 组件 -->
      <div class="section-block section-block--pkg">
        <div class="section-title">
          <span class="section-index">1</span>
          套餐名 &amp; 组件
        </div>

        <div class="pkg-form-item">
          <div class="pkg-label">套餐名：</div>
          <a-input v-model="form.productName" placeholder="如「1日门票+双餐」" />
        </div>

        <div class="pkg-form-item">
          <div class="pkg-label">组件 <span class="pkg-label-key">（票种 group / 加购项，每组件带份数）</span></div>

          <a-spin :spinning="packageGroupLoading">
            <div
              v-for="(comp, compIndex) in form.components"
              :key="compIndex"
              class="pkg-component-row"
            >
              <span class="pkg-field-label">组件：</span>
              <a-select
                v-model="comp.ticketMp"
                showSearch
                optionFilterProp="children"
                placeholder="请选择组件"
                class="pkg-component-select"
                :loading="packageGroupLoading"
                @change="val => onComponentGroupChange(comp, val)"
              >
                <a-select-opt-group
                  v-for="group in packageGroupOptions"
                  :key="group.label"
                  :label="group.label"
                >
                  <a-select-option
                    v-for="item in group.options"
                    :key="item.code"
                    :value="item.code"
                    :disabled="isPackageCodeTaken(comp, item.code)"
                  >
                    {{ item.code }} {{ item.name }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
              <span class="pkg-field-label">份数：</span>
              <a-input-number v-model="comp.quantity" :min="1" style="width: 72px" />
              <a-button
                type="danger"
                ghost
                shape="circle"
                icon="close"
                size="small"
                @click="onRemoveComponent(compIndex)"
              />
            </div>
          </a-spin>

          <a-button
            class="add-component-btn"
            type="dashed"
            block
            icon="plus"
            :loading="packageGroupLoading"
            @click="onAddComponent"
          >
            新增组件
          </a-button>
        </div>
      </div>

      <!-- 2. 保存 -->
      <div class="section-block section-block--pkg">
        <div class="section-title">
          <span class="section-index">2</span>
          保存
        </div>
        <a-button type="primary" block class="save-btn" :loading="saveLoading" @click="onSaveEvent">
          {{ isEdit ? '保存修改' : '保存产品' }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
export default {
  name: 'ProductCreateDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 编辑时传入列表行数据；新建时为 null */
    editRecord: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      // GET /hkdl/config/admin/scan/events 返回的 event 列表
      eventList: [],
      eventLoading: false,
      // GET /hkdl/config/admin/scan/package-groups
      packageGroupList: [],
      packageGroupLoading: false,
      // POST /hkdl/config/admin/scan/tickets
      ticketList: [],
      ticketLoading: false,
      saveLoading: false,
      productKey: '',
      form: this.createEmptyForm()
    }
  },
  computed: {
    isEdit () {
      return !!this.productKey
    },
    selectedEvent () {
      return this.eventList.find(event => String(event.eventId) === String(this.form.eventId))
    },
    currentEventType () {
      return (this.selectedEvent && this.selectedEvent.eventType) || this.form.eventType || ''
    },
    isRType () {
      return this.currentEventType === 'R'
    },
    optionNoticeText () {
      if (this.currentEventType === 'S') {
        return 'S 型：票只有完整名+底价，无票型字段；现票价仅占位。'
      }
      return 'R 型：票带明确票型(成人/小童/长者)+完整票名；ticket_map 走 identity（stable=mp=真 ticketid）。'
    },
    /** 套票组件下拉：按 日票/场次/加购项 分组 */
    packageGroupOptions () {
      const buckets = {
        '1D-S': { label: '1日票 · 日期场次', options: [] },
        '2D-S': { label: '2日票 · 日期场次', options: [] },
        '1D-R': { label: '1日票 · 免场次(任意日)', options: [] },
        '2D-R': { label: '2日票 · 免场次(任意日)', options: [] },
        addon: { label: '加购项', options: [] },
        other: { label: '其他', options: [] }
      }
      ;(this.packageGroupList || []).forEach(item => {
        const code = item.code || item.ticketMp || ''
        const option = {
          code,
          name: item.name || '',
          itemType: Number(item.itemType),
          eventType: item.eventType,
          eventMp: item.eventMp
        }
        if (Number(item.itemType) === 2) {
          buckets.addon.options.push(option)
          return
        }
        const dayKey = /^2D/i.test(code) ? '2D' : (/^1D/i.test(code) ? '1D' : '')
        const typeKey = (item.eventType === 'R' || /-R$/i.test(code)) ? 'R' : 'S'
        const bucketKey = dayKey ? `${dayKey}-${typeKey}` : 'other'
        buckets[bucketKey].options.push(option)
      })
      return Object.keys(buckets)
        .map(key => buckets[key])
        .filter(group => group.options.length > 0)
    },
    /** 可选票按 标准 / 小童·长者 / 其他 分组展示 */
    ticketGroupOptions () {
      const buckets = {
        standard: { key: 'standard', label: '标准', tickets: [] },
        child: { key: 'child', label: '小童 / 长者', tickets: [] },
        other: { key: 'other', label: '其他', tickets: [] }
      }
      ;(this.ticketList || []).forEach(ticket => {
        const key = this.resolveTicketCategory(ticket)
        buckets[key].tickets.push(ticket)
      })
      return Object.keys(buckets)
        .map(key => buckets[key])
        .filter(group => group.tickets.length > 0)
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.initDialog()
      }
    }
  },
  methods: {
    createEmptyForm () {
      return {
        type: 'SINGLE',
        eventId: undefined,
        eventType: '',
        productName: '',
        optionGroups: [],
        components: []
      }
    },
    createEmptyOptionGroup () {
      return {
        optionName: '',
        adults: 1,
        fallbackPriceHkd: null,
        addSalePrice: null,
        ticketIds: []
      }
    },
    createEmptyComponent () {
      return {
        itemType: undefined,
        ticketMp: undefined,
        name: '',
        quantity: 1
      }
    },
    initDialog () {
      this.ticketList = []
      this.packageGroupList = []
      this.eventList = []
      if (this.editRecord && this.editRecord.productKey) {
        this.fillFormFromRecord(this.editRecord)
        if (this.form.type === 'PKG') {
          this.fetchPackageGroups()
        } else {
          this.onFetchEvent().then(() => {
            if (this.selectedEvent) {
              this.form.eventType = this.selectedEvent.eventType
            }
            if (this.form.eventId) {
              this.fetchTickets({ keepOptionGroups: true })
            }
          })
        }
      } else {
        this.productKey = ''
        this.form = this.createEmptyForm()
        // 新建默认 SINGLE：打开弹窗时自动拉取 event 列表
        this.onFetchEvent()
      }
    },
    fillFormFromRecord (record) {
      this.productKey = record.productKey || ''
      const type = record.type === 'PKG' ? 'PKG' : 'SINGLE'
      if (type === 'PKG') {
        this.form = {
          type: 'PKG',
          eventId: undefined,
          eventType: '',
          productName: record.productName || '',
          optionGroups: [],
          components: (record.components || []).map(comp => ({
            ticketMp: comp.ticketMp,
            itemType: Number(comp.itemType),
            name: comp.name || '',
            quantity: comp.quantity != null ? Number(comp.quantity) : 1
          }))
        }
        if (!this.form.components.length) {
          this.form.components = [this.createEmptyComponent()]
        }
        return
      }
      this.form = {
        type: 'SINGLE',
        eventId: record.eventId,
        eventType: record.eventType || '',
        productName: record.productName || '',
        optionGroups: (record.options || []).map((opt, index) => ({
          optionName: opt.optionName || `票型 ${index + 1}`,
          adults: opt.adults != null ? opt.adults : (opt.adultCount != null ? opt.adultCount : 1),
          fallbackPriceHkd: opt.fallbackPriceHkd != null ? opt.fallbackPriceHkd : null,
          addSalePrice: opt.addSalePrice != null ? opt.addSalePrice : null,
          ticketIds: (opt.ticketIds || opt.ticketMps || []).map(id => String(id))
        })),
        components: []
      }
    },
    onTypeChange () {
      // 编辑时类型不可改；新建切换时清空对方表单字段
      if (this.isEdit) return
      if (this.form.type === 'PKG') {
        this.form.eventId = undefined
        this.form.eventType = ''
        this.form.optionGroups = []
        this.ticketList = []
        this.form.components = [this.createEmptyComponent()]
        this.fetchPackageGroups()
      } else {
        this.form.components = []
        this.packageGroupList = []
      }
      this.form.productName = ''
    },
    onEventChange () {
      this.form.optionGroups = []
      this.ticketList = []
      this.form.eventType = this.selectedEvent ? this.selectedEvent.eventType : ''
      if (!this.form.eventId) return
      this.fetchTickets()
    },
    /**
     * 票分类：优先 ticketType；S 型无票型字段时从 name 推断
     * @returns {'standard'|'child'|'other'}
     */
    resolveTicketCategory (ticket) {
      const type = String(ticket.ticketType || '').trim().toLowerCase()
      if (type) {
        if (/成人|标准|adult|standard|ge\b/.test(type)) return 'standard'
        if (/小童|儿童|长者|child|senior|ch\b/.test(type)) return 'child'
      }
      const name = String(ticket.name || '')
      // 小童/儿童/长者优先（含「小童/长者」合并票、儿童餐券）
      if (/小童|儿童|长者/.test(name)) return 'child'
      if (/标准|成人/.test(name)) return 'standard'
      return 'other'
    },
    isTicketSelected (group, ticketId) {
      return group.ticketIds.some(id => String(id) === String(ticketId))
    },
    isTicketTakenByOther (group, ticketId) {
      return this.form.optionGroups.some(item => {
        if (item === group) return false
        return item.ticketIds.some(id => String(id) === String(ticketId))
      })
    },
    selectedTicketText (group) {
      if (!group.ticketIds.length) return '未选'
      return group.ticketIds
        .map(id => {
          const ticket = this.ticketList.find(item => String(item.ticketId) === String(id))
          return ticket ? ticket.ticketId : id
        })
        .join('、')
    },
    onToggleTicket (group, ticket) {
      const ticketId = String(ticket.ticketId)
      if (this.isTicketTakenByOther(group, ticketId)) return
      const index = group.ticketIds.findIndex(id => String(id) === ticketId)
      if (index === -1) {
        group.ticketIds.push(ticketId)
      } else {
        group.ticketIds.splice(index, 1)
      }
    },
    onAddOptionGroup () {
      if (!this.form.eventId) {
        this.$message.warning('请先选择产品 event')
        return
      }
      this.form.optionGroups.push(this.createEmptyOptionGroup())
    },
    onRemoveOptionGroup (groupIndex) {
      this.form.optionGroups.splice(groupIndex, 1)
    },
    isPackageCodeTaken (comp, code) {
      return this.form.components.some(item => {
        if (item === comp) return false
        return item.ticketMp === code
      })
    },
    onComponentGroupChange (comp, val) {
      const group = this.packageGroupList.find(item => (item.code || item.ticketMp) === val)
      if (!group) return
      comp.ticketMp = group.code || group.ticketMp
      comp.name = group.name || ''
      comp.itemType = Number(group.itemType)
    },
    onAddComponent () {
      const pushRow = () => {
        this.form.components.push(this.createEmptyComponent())
      }
      if (!this.packageGroupList.length && !this.packageGroupLoading) {
        this.fetchPackageGroups().then(pushRow)
        return
      }
      pushRow()
    },
    onRemoveComponent (compIndex) {
      if (this.form.components.length <= 1) {
        this.$message.warning('至少保留一个组件')
        return
      }
      this.form.components.splice(compIndex, 1)
    },
    /**
     * 拉取 event 列表
     * GET /hkdl/config/admin/scan/events
     * BaseService 已在 status == 1 时 resolve 内层 data（数组 [{eventId, name, eventType}]）
     */
    onFetchEvent () {
      this.eventLoading = true
      return this.getHttpServices().getSourceService()
        .get('/hkdl/config/admin/scan/events')
        .then(list => {
          this.eventList = Array.isArray(list) ? list : []
          if (!this.eventList.length) {
            this.$message.warning('未获取到可用 event')
          }
        })
        .catch(err => {
          this.eventList = []
          this.$message.error((err && err.msg) || '获取 event 失败，请重试！')
        })
        .finally(() => {
          this.eventLoading = false
        })
    },
    /**
     * 选择 event 后拉取可选票
     * POST /hkdl/config/admin/scan/tickets
     * body: { eventId, eventType }
     * data: [{ ticketId, ticketMp, name, ticketType, price }]
     */
    fetchTickets (options = {}) {
      const event = this.selectedEvent
      const eventId = event ? event.eventId : this.form.eventId
      const eventType = event ? event.eventType : this.form.eventType
      if (!eventId || !eventType) {
        this.$message.warning('未找到所选 event，请重新拉取')
        return Promise.resolve()
      }
      this.ticketLoading = true
      return this.getHttpServices().getSourceService()
        .post('/hkdl/config/admin/scan/tickets', {
          eventId,
          eventType
        })
        .then(list => {
          this.ticketList = Array.isArray(list) ? list : []
          if (!this.ticketList.length) {
            this.$message.warning('未获取到可选票')
          } else if (!options.keepOptionGroups && !this.form.optionGroups.length) {
            // 有票时默认放一个空票型，方便直接勾选
            this.form.optionGroups.push(this.createEmptyOptionGroup())
          }
        })
        .catch(err => {
          this.ticketList = []
          this.$message.error((err && err.msg) || '获取票型失败，请重试！')
        })
        .finally(() => {
          this.ticketLoading = false
        })
    },
    /**
     * 选择套票后拉取可打包组件
     * GET /hkdl/config/admin/scan/package-groups
     * data: [{ code, name, itemType, eventMp, eventType }]
     */
    fetchPackageGroups () {
      this.packageGroupLoading = true
      return this.getHttpServices().getSourceService()
        .get('/hkdl/config/admin/scan/package-groups')
        .then(list => {
          this.packageGroupList = Array.isArray(list) ? list : (list && list.list) || []
          if (!this.packageGroupList.length) {
            this.$message.warning('未获取到可用套餐组件')
          }
        })
        .catch(err => {
          this.packageGroupList = []
          this.$message.error((err && err.msg) || '获取套餐组件失败，请重试！')
        })
        .finally(() => {
          this.packageGroupLoading = false
        })
    },
    buildSinglePayload () {
      const event = this.selectedEvent
      const eventId = event ? event.eventId : this.form.eventId
      const eventType = event ? event.eventType : this.form.eventType
      const isR = eventType === 'R'
      const productName = (this.form.productName || '').trim()
        || (this.editRecord && this.editRecord.productName)
        || ''
      return {
        type: 'SINGLE',
        productName,
        eventId: String(eventId),
        eventType,
        options: this.form.optionGroups.map((group, index) => ({
          optionName: group.optionName || `票型 ${index + 1}`,
          adults: group.adults != null ? Number(group.adults) : 1,
          fallbackPriceHkd: isR && group.fallbackPriceHkd != null && group.fallbackPriceHkd !== ''
            ? Number(group.fallbackPriceHkd)
            : null,
          addSalePrice: group.addSalePrice != null && group.addSalePrice !== ''
            ? Number(group.addSalePrice)
            : null,
          ticketIds: group.ticketIds.map(id => String(id))
        }))
      }
    },
    buildPkgPayload () {
      return {
        type: 'PKG',
        productName: this.form.productName.trim(),
        components: this.form.components.map(comp => ({
          ticketMp: String(comp.ticketMp),
          itemType: Number(comp.itemType),
          quantity: Number(comp.quantity)
        }))
      }
    },
    validateSingleForm () {
      const eventType = this.currentEventType
      if (!this.form.eventId || !eventType) {
        this.$message.warning('请先选择产品 event')
        return false
      }
      if (!this.isEdit && (!this.form.productName || !this.form.productName.trim())) {
        this.$message.warning('请填写产品名')
        return false
      }
      if (!this.form.optionGroups.length) {
        this.$message.warning('请至少新增一个票型')
        return false
      }
      const emptyGroup = this.form.optionGroups.some(group => !group.ticketIds.length)
      if (emptyGroup) {
        this.$message.warning('每个票型请至少选择一张票')
        return false
      }
      if (eventType === 'R') {
        const missingFallback = this.form.optionGroups.some(group => {
          return group.fallbackPriceHkd == null || group.fallbackPriceHkd === ''
        })
        if (missingFallback) {
          this.$message.warning('每个票型请填写兜底价')
          return false
        }
      }
      return true
    },
    validatePkgForm () {
      if (!this.form.productName || !this.form.productName.trim()) {
        this.$message.warning('请填写套餐名')
        return false
      }
      if (!this.form.components.length) {
        this.$message.warning('请至少新增一个组件')
        return false
      }
      const incomplete = this.form.components.some(c => !c.ticketMp || !c.itemType || !c.quantity)
      if (incomplete) {
        this.$message.warning('请完善每个组件的选择与份数')
        return false
      }
      return true
    },
    /**
     * 新建: POST /hkdl/config/admin/products
     * 编辑: PUT /hkdl/config/admin/products/{productKey}
     */
    onSaveEvent () {
      const isPkg = this.form.type === 'PKG'
      if (isPkg ? !this.validatePkgForm() : !this.validateSingleForm()) return

      const payload = isPkg ? this.buildPkgPayload() : this.buildSinglePayload()
      const service = this.getHttpServices().getSourceService()
      const request = this.isEdit
        ? service.put(`/hkdl/config/admin/products/${this.productKey}`, payload)
        : service.post('/hkdl/config/admin/products', payload)

      this.saveLoading = true
      request
        .then(() => {
          this.$message.success(this.isEdit ? '修改成功' : '建品成功')
          this.$emit('success')
          this.handleClose()
        })
        .catch(err => {
          this.$message.error((err && err.msg) || (this.isEdit ? '修改失败，请重试！' : '建品失败，请重试！'))
        })
        .finally(() => {
          this.saveLoading = false
        })
    },
    handleClose () {
      this.productKey = ''
      this.form = this.createEmptyForm()
      this.ticketList = []
      this.packageGroupList = []
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="less">
.type-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;

  .type-label {
    margin-right: 8px;
    color: rgba(0, 0, 0, 0.85);
  }
}

.section-block {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fff;

  &--pkg {
    border-style: dashed;
    border-color: #d9d9d9;
  }
}

.section-title {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);

  .section-index {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 8px;
    line-height: 20px;
    border-radius: 50%;
    background: #fa8c16;
    color: #fff;
    font-size: 13px;
    text-align: center;
  }

  .section-tip {
    font-size: 12px;
    font-weight: normal;
    color: rgba(0, 0, 0, 0.45);
  }
}

.event-row {
  display: flex;
  gap: 12px;
}

.event-info {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);

  b {
    color: #fa8c16;
  }
}

.option-notice {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff7e6;
  color: #d46b08;
  font-size: 13px;
}

.option-card {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background: #fafafa;
}

.option-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .option-card-title {
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
  }

  .option-card-title-input {
    flex: 1;
    min-width: 120px;
    max-width: 280px;
    font-weight: 500;
  }
}

.option-card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.fallback-inline {
  display: flex;
  align-items: center;
  gap: 6px;

  .fallback-inline-label {
    font-size: 13px;
    font-weight: normal;
    color: rgba(0, 0, 0, 0.85);
    white-space: nowrap;
  }
}

.ticket-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ticket-group {
  padding: 10px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background: #fff;
}

.ticket-group-label {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #d46b08;
  line-height: 1.4;

  &::before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 12px;
    margin-right: 6px;
    border-radius: 2px;
    background: #fa8c16;
    vertical-align: -1px;
  }
}

.ticket-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-empty {
  margin-bottom: 12px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}

.ticket-chip {
  padding: 4px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  transition: all 0.2s;

  .ticket-id,
  .ticket-price {
    margin-left: 6px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  &:hover:not(.is-disabled) {
    border-color: #fa8c16;
    color: #fa8c16;
  }

  &.is-selected {
    border-color: #fa8c16;
    background: #fff7e6;
    color: #fa8c16;

    .ticket-id,
    .ticket-price {
      color: #d46b08;
    }
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.45;
    background: #f5f5f5;
  }
}

.ticket-selected-info {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.add-option-btn {
  margin-top: 4px;
  color: #fa8c16;
  border-color: #ffd591;

  &:hover {
    color: #d46b08;
    border-color: #fa8c16;
  }
}

.option-footnote {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);

  b {
    color: #fa8c16;
  }
}

.save-form-item {
  margin-bottom: 12px;

  .save-label {
    margin-bottom: 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
  }
}

.save-btn {
  margin-top: 4px;
  background: #fa8c16;
  border-color: #fa8c16;

  &:hover,
  &:focus {
    background: #ffa940;
    border-color: #ffa940;
  }
}

/* ---- PKG 套餐专属样式 ---- */
.pkg-form-item {
  margin-bottom: 16px;
}

.pkg-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.85);

  .pkg-label-key {
    color: rgba(0, 0, 0, 0.45);
  }
}

.pkg-component-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.pkg-field-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
}

.pkg-component-select {
  flex: 1;
  min-width: 260px;
}

.add-component-btn {
  color: #fa8c16;
  border-color: #ffd591;

  &:hover,
  &:focus {
    color: #d46b08;
    border-color: #fa8c16;
  }
}

.pkg-footnote {
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.45);

  b {
    color: #fa8c16;
    font-weight: 500;
  }
}
</style>

<style lang="less">
.product-create-dialog {
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
}
</style>
